import { useEffect, useState } from "react";
import { api, formatEuro } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Gavel, Calendar, Buildings } from "@phosphor-icons/react";
import WatchButton from "@/components/WatchButton";
import PdfButton from "@/components/PdfButton";
import CityCombobox from "@/components/CityCombobox";

export default function Aste() {
  const [cities, setCities] = useState([]);
  const [city, setCity] = useState("milano");
  const [items, setItems] = useState([]);
  const [minDiscount, setMinDiscount] = useState(25);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/cities").then((r) => setCities(r.data)); }, []);

  useEffect(() => {
    setLoading(true);
    api.get(`/auctions?city=${city}&min_discount=${minDiscount}`)
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, [city, minDiscount]);

  return (
    <div className="min-h-[calc(100vh-56px)] bg-zinc-950">
      <div className="border-b border-white/10 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="font-mono-data text-xs text-amber-400 tracking-widest mb-2">— ASTE & PIGNORAMENTI</div>
          <h1 className="font-display font-black text-3xl mb-6">Occasioni giudiziarie</h1>

          <div className="flex flex-wrap items-end gap-6">
            <div className="min-w-[220px]">
              <Label className="font-mono-data text-[10px] text-zinc-400 tracking-widest">CITTÀ</Label>
              <div className="mt-1">
                <CityCombobox value={city} onChange={setCity} cities={cities} testId="auctions-city-select" />
              </div>
            </div>
            <div className="min-w-[260px]">
              <div className="flex items-center justify-between mb-1">
                <Label className="font-mono-data text-[10px] text-zinc-400 tracking-widest">SCONTO MINIMO</Label>
                <span className="font-mono-data text-xs text-amber-400 font-bold">{minDiscount}%</span>
              </div>
              <Slider value={[minDiscount]} onValueChange={(v) => setMinDiscount(v[0])} min={0} max={55} step={5} data-testid="slider-discount" />
            </div>
            <div className="ml-auto font-mono-data text-xs text-zinc-500" data-testid="auctions-count">
              {loading ? "..." : `${items.length} aste`}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        <div className="border border-white/10" data-testid="auctions-list">
          <div className="grid grid-cols-12 gap-4 px-4 py-2 border-b border-white/10 bg-zinc-900/50 font-mono-data text-[10px] text-zinc-500 tracking-widest">
            <div className="col-span-3">IMMOBILE</div>
            <div className="col-span-2 text-right">BASE D'ASTA</div>
            <div className="col-span-2 text-right">VALORE MERCATO</div>
            <div className="col-span-1 text-right">SCONTO</div>
            <div className="col-span-2">DATA</div>
            <div className="col-span-1">M²</div>
            <div className="col-span-1 text-right">AZIONI</div>
          </div>
          {items.map((a) => (
            <div key={a.id} className="grid grid-cols-12 gap-4 px-4 py-3 border-b border-white/5 hover:bg-white/[0.03] transition-colors font-mono-data text-sm items-center" data-testid={`auction-row-${a.id}`}>
              <div className="col-span-3">
                <div className="flex items-center gap-2 mb-0.5">
                  <Gavel size={12} weight="bold" className="text-amber-400" />
                  <span className="text-zinc-100">{a.type} · {a.zone}</span>
                </div>
                <div className="text-[11px] text-zinc-500">{a.court} · {a.procedure} {a.procedure_number}</div>
              </div>
              <div className="col-span-2 text-right text-amber-400 font-bold">{formatEuro(a.base_price)}</div>
              <div className="col-span-2 text-right text-zinc-400 line-through">{formatEuro(a.market_price)}</div>
              <div className="col-span-1 text-right">
                <Badge className="rounded-none bg-amber-400 text-amber-950 font-mono-data text-[10px] font-bold hover:bg-amber-400">-{a.discount_pct}%</Badge>
              </div>
              <div className="col-span-2 text-zinc-300 flex items-center gap-1.5">
                <Calendar size={11} weight="bold" className="text-zinc-500" />{a.auction_date}
              </div>
              <div className="col-span-1 text-zinc-400">{a.sqm}</div>
              <div className="col-span-1 flex items-center gap-1 justify-end">
                <WatchButton kind="auction" id={a.id} city={a.city} />
                <PdfButton kind="auction" id={a.id} label="" />
              </div>
            </div>
          ))}
          {!loading && items.length === 0 && (
            <div className="p-12 text-center font-mono-data text-sm text-zinc-500">Nessuna asta corrisponde ai filtri</div>
          )}
        </div>

        <div className="mt-6 border border-white/10 p-4 flex items-start gap-3 bg-zinc-900/40">
          <Buildings size={20} weight="bold" className="text-amber-400 shrink-0 mt-0.5" />
          <div className="text-xs text-zinc-400 leading-relaxed">
            <span className="text-zinc-100 font-semibold">Fonti ufficiali:</span> per verifica di dettaglio e partecipazione ad aste consulta il {" "}
            <a href="https://pvp.giustizia.it" target="_blank" rel="noreferrer" className="text-amber-400 hover:underline">Portale Vendite Pubbliche</a> del Ministero della Giustizia.
            I dati mostrati sono simulati per finalità di demo.
          </div>
        </div>
      </div>
    </div>
  );
}
